import { Container, Graphics } from "pixi.js";

import { Sea, waveHeightAt, WAVE_HEIGHT } from "./Waves";

/**
 * The water a bow throws, as particles.
 *
 * It was written up as blocked on art, and the route was the blocker rather than the effect: the
 * expected path was an animation in the `fxA` sheet routed by name through `config/fx.json`, and
 * there is no spray in that sheet. **This is the same effect without the art** — a few hundred
 * droplets
 * on a ring buffer, drawn as circles, which costs one `Graphics` rebuild a frame and no asset at
 * all. A sprite sheet can replace it later without any caller noticing.
 *
 * ## What makes it worth having
 *
 * Not speed. A hull that threw the same plume at the same speed forever would be a decoration
 * bolted to the bow, and there is one of those already — the wake, which says the same thing about
 * the same number from the other end of the boat.
 *
 * **It is the sea doing something to the ship.** The emission is driven by how hard the bow is
 * being pushed *into the water in front of it*: the wave field is read at the bow and at the hull's
 * middle, and a bow sitting on higher water than its own waist is a bow burying into the face of a
 * wave. That is exactly when a real hull throws water, it is the one moment in a swell that a
 * top-down camera can otherwise not see, and it costs two evaluations of a formula the client is
 * already running for the shader and for every hull's lift (`Waves.ts`).
 *
 * So the plume comes in bursts as the boat drives through a sea, and a boat sitting in a calm
 * makes almost none. Which is the answer to the question phase 6 was really asking — what does a
 * wave *do* to a ship, now that it does not push one.
 *
 * ## Coordinates
 *
 * View space, and scrolled by the camera exactly as `SeaLife` and `WakeField` are. A droplet is a
 * point on the water, so a boat sailing east has to leave it behind rather than tow it along; and
 * because the caller hands over the position a hull is *drawn* at, nothing in here has to learn
 * that the arena loops.
 */

/** Colour of a droplet — the same white water the wake and the whitecaps are. See Water.ts. */
const SPRAY_COLOR = 0xeaf6ff;

/**
 * How many droplets may be alive at once, across every boat on screen.
 *
 * A ceiling rather than a target, and the oldest go back when it is reached — the same bargain
 * `WakeField` makes with its own budget, and for the same reason: a busy arena must not be able
 * to spend an unbounded number of circles on the least important thing on the water.
 */
const BUDGET = 420;

/**
 * How long a droplet lives, in seconds, and how much of that is spent at full size.
 *
 * Short. Spray is the fastest thing on this sea and the only one that is *airborne*, so it has to
 * be gone before the eye starts reading it as foam — the wake already owns everything that lingers.
 */
const LIFE = 0.42;

/** Radius when it leaves the bow and at the end of its life, in view pixels. */
const SIZE_START = 5.2;
const SIZE_END = 0.7;

/**
 * How fast the bow has to be moving before it throws anything, and the speed the plume is at its
 * fullest, both in view pixels per second.
 *
 * The floor is well under a hull's cruise: a boat in irons still makes 50 and should still be
 * working, just quietly. The ceiling is above what any hull sails at without the turbo, so lighting
 * it is visible in the water at the bow as well as in the coins astern.
 */
const SPEED_FLOOR = 45;
const SPEED_FULL = 150;

/**
 * Droplets a second at full speed on flat water, per hull, and how much a bow burying into a wave
 * multiplies that.
 *
 * The multiplier is the whole effect. On a flat sea the plume is a thin dribble that says "this
 * ship is moving"; driving into the face of a crest it is four times that for as long as the bow
 * is in it, which is what makes a boat look like it is fighting the water rather than sliding over
 * a picture of it.
 */
const RATE = 170;
const BURY_GAIN = 2.5;

/**
 * How far ahead of the hull's middle the water is sampled, as a fraction of the hull's length, and
 * how much of a wave's height counts as fully buried.
 *
 * The sample sits a little *beyond* the stem rather than on it, because what decides whether a bow
 * is climbing is the water it is about to hit. The threshold is a fraction of `WAVE_HEIGHT` so it
 * keeps meaning the same thing when the dial moves — on a flat calm nothing ever buries, which is
 * correct rather than a special case.
 */
const BOW_REACH = 0.46;
const BURY_FULL = 0.22;

/**
 * How the droplets leave: how much of the hull's own speed they carry forward, how hard they are
 * thrown sideways, and how wide the fan is in radians.
 *
 * Sideways rather than forward is what makes it read as spray instead of exhaust. A droplet that
 * kept the hull's speed would sit at the bow looking pinned to it; thrown out and slightly ahead,
 * the plume opens into the V a moving hull actually pushes.
 */
const CARRY = 0.2;
const THROW_OUT = 210;
const FAN = 0.9;

/** How much of its speed a droplet keeps each second — air, and then the water it falls back into. */
const DRAG = 0.25;

/** One airborne droplet. Plain data on a ring buffer; nothing here is a display object. */
interface Drop {
    x: number;
    y: number;
    velocityX: number;
    velocityY: number;
    age: number;
    /** 0 to 1 — how hard the bow was working when this left it. Scales the size. */
    force: number;
}

/** What one hull's bow was doing last frame, so this one can measure how far it moved. */
interface Bow {
    x: number;
    y: number;
    /** Droplets owed but not yet whole. Kept so a slow emission rate is not rounded away to none. */
    owed: number;
    idle: number;
}

export class SprayField {
    private readonly _container = new Container();
    private readonly _drops = new Graphics();

    /** The ring: `_head` is the oldest live droplet, `_count` how many follow it. */
    private readonly _pool: Drop[] = [];
    private _head = 0;
    private _count = 0;

    private readonly _bows = new Map<string, Bow>();

    private _width = 0;
    private _height = 0;

    public get container(): Container {
        return this._container;
    }

    constructor() {
        this._container.addChild(this._drops);
        for (let i = 0; i < BUDGET; i++) this._pool.push({ x: 0, y: 0, velocityX: 0, velocityY: 0, age: 0, force: 0 });
    }

    /**
     * Reports where one hull is this frame and throws whatever water its bow has earned. Called for
     * **every** boat on screen, the local one included, exactly like `WakeField.trail`.
     *
     * `x`/`y` are the hull's centre in view space — the coordinates it is drawn at — and `sea` is
     * the client's own wave field, read at the bow to find out whether it is burying.
     */
    public spray(id: string, x: number, y: number, heading: number, length: number, sea: Sea, deltaSeconds: number): void {
        // Heading 0 is north, so the bow is (sin, -cos).
        const aheadX = Math.sin(heading);
        const aheadY = -Math.cos(heading);
        const bowX = x + aheadX * length * BOW_REACH;
        const bowY = y + aheadY * length * BOW_REACH;

        const bow = this._bows.get(id);
        if (!bow) {
            this._bows.set(id, { x: bowX, y: bowY, owed: 0, idle: 0 });
            // Nothing on the first report: there is no previous point to measure a speed from,
            // and treating the origin as one would have every hull arrive at a full gallop.
            return;
        }

        bow.idle = 0;
        const travelled = Math.hypot(bowX - bow.x, bowY - bow.y);
        bow.x = bowX;
        bow.y = bowY;

        // A jump this far is a respawn or a hull coming into view, not a fast frame.
        if (deltaSeconds <= 0 || travelled > length) return;

        const speed = travelled / deltaSeconds;
        const drive = Math.min(1, Math.max(0, (speed - SPEED_FLOOR) / (SPEED_FULL - SPEED_FLOOR)));
        if (drive <= 0) return;

        // How much higher the water is where the bow is going than under the hull's waist. The
        // field is measured from the camera, and these are view coordinates, so the offset is
        // simply the distance from the middle of the screen.
        const centreX = this._width / 2;
        const centreY = this._height / 2;
        const rise = waveHeightAt(sea, bowX - centreX, bowY - centreY) - waveHeightAt(sea, x - centreX, y - centreY);
        const bury = Math.min(1, Math.max(0, (rise * WAVE_HEIGHT) / BURY_FULL));

        bow.owed += RATE * drive * (1 + bury * BURY_GAIN) * deltaSeconds;

        // How hard *this* droplet was thrown, as opposed to how many there are. Kept separate
        // from the emission rate on purpose: burying the bow should throw more water **and**
        // throw it harder, and one number scaled by `BURY_GAIN` cannot say both without coming
        // out at a fifth of its range on flat water, which is what the first draft did.
        const force = Math.min(1, drive + bury * 0.7);

        while (bow.owed >= 1) {
            bow.owed -= 1;
            this._throw(bowX, bowY, aheadX, aheadY, speed, force);
        }
    }

    /** Ages every droplet, carries it on, and redraws the field. */
    public update(deltaSeconds: number): void {
        // Every droplet is given the same life, so they die in the order they were thrown and the
        // dead are always a run at the head of the ring.
        while (this._count > 0 && this._pool[this._head].age >= LIFE) {
            this._head = (this._head + 1) % BUDGET;
            this._count--;
        }

        this._drops.clear();

        for (let step = 0; step < this._count; step++) {
            const drop = this._pool[(this._head + step) % BUDGET];

            drop.age += deltaSeconds;
            drop.x += drop.velocityX * deltaSeconds;
            drop.y += drop.velocityY * deltaSeconds;

            // Framerate-independent drag, the same shape as every other ease in the game.
            const kept = Math.pow(DRAG, deltaSeconds);
            drop.velocityX *= kept;
            drop.velocityY *= kept;

            const left = 1 - drop.age / LIFE;
            if (left <= 0) continue;

            // Shrinks and fades together: a droplet that only faded would read as a stain, and one
            // that only shrank would pop out of existence at full brightness.
            const radius = (SIZE_END + (SIZE_START - SIZE_END) * left) * (0.55 + drop.force * 0.45);
            this._drops.circle(drop.x, drop.y, radius).fill({ color: SPRAY_COLOR, alpha: left });
        }

        for (const [id, bow] of this._bows) {
            bow.idle += deltaSeconds;
            // Gone for longer than a droplet lasts, so there is nothing of this hull in the air to
            // be continuous with. Anything else would leak a bow per ship per battle.
            if (bow.idle > LIFE) this._bows.delete(id);
        }
    }

    /**
     * Slides every droplet by a world offset, exactly as `WakeField.scroll` slides the trail. Spray
     * is water, not a decal on the camera: a boat sailing east leaves its own plume behind.
     *
     * The bows move with it for the same reason the wake's tracks do — a bow is a point on the
     * water, and leaving it where the camera was would measure the next frame's speed from a place
     * the sea has already left.
     */
    public scroll(deltaX: number, deltaY: number): void {
        for (let step = 0; step < this._count; step++) {
            const drop = this._pool[(this._head + step) % BUDGET];
            drop.x -= deltaX;
            drop.y -= deltaY;
        }

        for (const bow of this._bows.values()) {
            bow.x -= deltaX;
            bow.y -= deltaY;
        }
    }

    public resize(width: number, height: number): void {
        this._width = width;
        this._height = height;
    }

    public destroy(): void {
        this._container.destroy({ children: true });
    }

    /**
     * Throws one droplet off the bow: forward with a share of the hull's speed, and outward to
     * whichever side the fan picked. Taken from the ring's tail, so a full field drops its oldest
     * droplet rather than growing.
     */
    private _throw(x: number, y: number, aheadX: number, aheadY: number, speed: number, force: number): void {
        const drop = this._pool[(this._head + this._count) % BUDGET];
        if (this._count < BUDGET) {
            this._count++;
        } else {
            this._head = (this._head + 1) % BUDGET;
        }

        // Either side of the bow, at a random angle inside the fan. Squared, so most droplets go
        // out near the beam and the ones straight ahead are the exception — which is the shape a
        // hull's own bow wave has.
        const side = Math.random() < 0.5 ? -1 : 1;
        const angle = FAN * (0.35 + Math.random() * 0.65);
        const outX = aheadX * Math.cos(angle) - aheadY * Math.sin(angle) * side;
        const outY = aheadY * Math.cos(angle) + aheadX * Math.sin(angle) * side;

        const thrown = THROW_OUT * (0.45 + force * 0.55) * (0.6 + Math.random() * 0.8);

        drop.x = x;
        drop.y = y;
        drop.velocityX = aheadX * speed * CARRY + outX * thrown;
        drop.velocityY = aheadY * speed * CARRY + outY * thrown;
        drop.age = 0;
        drop.force = force;
    }
}
